import {
  findClosestPerimeterPoint,
  LinesetPerimeterPoint,
  precisePoint
} from '@alpinehomeair/common';
import { PositionType } from 'data/types';
import {
  LinesetSnapOptionsType,
  LinesetSnapResultType
} from 'utils/lineset-snap-types';

export const LINESET_EDGE_SNAP_FT = 0.5;
export const LINESET_ALONG_EDGE_STEP_FT = 1;

export function snapToLattice(point: PositionType): PositionType {
  return { x: Math.round(point.x), y: Math.round(point.y) };
}

function perimeterEdge(
  perimeters: PositionType[][],
  entry: LinesetPerimeterPoint
): { a: PositionType; b: PositionType } {
  const ring = perimeters[entry.ringIndex];
  return {
    a: ring[entry.edgeIndex],
    b: ring[(entry.edgeIndex + 1) % ring.length]
  };
}

export function steppedPointOnSegment(
  a: PositionType,
  b: PositionType,
  point: PositionType,
  stepFt: number
): PositionType {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return { x: a.x, y: a.y };
  }
  const along = ((point.x - a.x) * dx + (point.y - a.y) * dy) / length;
  const stepped = stepFt > 0 ? Math.round(along / stepFt) * stepFt : along;
  const clamped = Math.min(length, Math.max(0, stepped));
  return {
    x: a.x + (dx / length) * clamped,
    y: a.y + (dy / length) * clamped
  };
}

export function snapLinesetPointDetailed(
  point: PositionType,
  perimeters: PositionType[][],
  options: LinesetSnapOptionsType = {}
): LinesetSnapResultType {
  const snapDistanceFt = options.snapDistanceFt ?? LINESET_EDGE_SNAP_FT;
  const stepFt = options.alongEdgeStepFt ?? LINESET_ALONG_EDGE_STEP_FT;
  const entry = findClosestPerimeterPoint(point, perimeters);
  if (!entry) {
    return { point: snapToLattice(point), onPerimeter: false };
  }
  const distance = Math.hypot(point.x - entry.point.x, point.y - entry.point.y);
  if (distance > snapDistanceFt) {
    return { point: snapToLattice(point), onPerimeter: false };
  }
  const { a, b } = perimeterEdge(perimeters, entry);
  return {
    point: precisePoint(steppedPointOnSegment(a, b, entry.point, stepFt)),
    onPerimeter: true
  };
}

export function snapLinesetPoint(
  point: PositionType,
  perimeters: PositionType[][],
  options: LinesetSnapOptionsType = {}
): PositionType {
  return snapLinesetPointDetailed(point, perimeters, options).point;
}

export function snapLinesetPointToSegment(
  point: PositionType,
  a: PositionType,
  b: PositionType,
  perimeters: PositionType[][],
  options: LinesetSnapOptionsType = {}
): PositionType {
  const snapped = snapLinesetPointDetailed(point, perimeters, options);
  if (snapped.onPerimeter) {
    return snapped.point;
  }
  const stepFt = options.alongEdgeStepFt ?? LINESET_ALONG_EDGE_STEP_FT;
  return precisePoint(steppedPointOnSegment(a, b, point, stepFt));
}
